import moment from "moment";
import React, { useState, useEffect, useMemo } from "react";
import { Autocomplete } from "@material-ui/lab";
import { useDispatch, useSelector } from "react-redux";
import {
  TextField,
  Card,
  Grid,
  Box,
  Typography,
  MenuItem,
  Divider,
  Button,
  IconButton,
  Popover,
  Chip,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Select,
} from "@material-ui/core";
import {
  Add,
  Close,
  Delete,
  Edit,
  Replay,
  Save,
  Person,
  GroupAdd,
} from "@material-ui/icons";

import { filterCouncil, insertCouncil, updateCouncil, deleteCouncil } from "../../reducer/council/action";
import { notification } from "../../reducer/home/action";
import { getAllProfile } from "../../reducer/profile/action";
import {
  COUNCIL_ROLES,
  buildDefaultMembers,
  groupMembersByRole,
  getRoleConfig,
  validateMembers,
} from "../../constants/councilRoles";
import select from "../../utils/select";
import theme from "../../theme";
import { getCurrentSchoolYear } from "containers/User/TimeResearch/utils";

// StudentProjectCouncil — page quản lý template "tiểu ban" (hội đồng tái sử
// dụng). Refactor 2026-05:
//   - Schema mới: 1 template = { name, schoolYear, councilType, members[] }
//     trong đó members[] là array có role_key + order_num, không còn 30+ cột
//     hardcode.
//   - UI mới: list trái (table tiểu ban) + form phải (edit metadata + members
//     với +/- button cho ủy viên / phản biện).
//   - Vai trò + max count đọc từ `constants/councilRoles.js` — đổi cấu trúc
//     không cần đụng BE.
//
// Link với đề tài: thông qua bảng student_research_council_assignments khi
// admin "Gán hội đồng" cho 1 đề tài cụ thể (xem CouncilForm).
function StudentProjectCouncil() {
  const dispatch = useDispatch();

  const councilList = useSelector((s) => select(s, "councilReducer", "council")) || [];
  const profileList = useSelector((s) => select(s, "profileReducer", "list")) || [];

  const [year, setYear] = useState(getCurrentSchoolYear());
  const [councilType, setCouncilType] = useState("final");
  // selected = template đang xem/sửa. null = đang ở mode tạo mới.
  const [selected, setSelected] = useState(null);
  const [mode, setMode] = useState("view"); // view | edit | create
  const [form, setForm] = useState(null); // current form state when editing/creating
  const [deleteAnchor, setDeleteAnchor] = useState(null);
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  const yearRange = useMemo(() => {
    const arr = [];
    for (let i = parseInt(moment().format("YYYY")); i >= 2021; i--) arr.push(i);
    return arr;
  }, []);

  const reload = () => {
    dispatch(getAllProfile());
    // Refactor 2026-05-16: scope theo module để tách pool template SV vs Cơ sở.
    dispatch(filterCouncil({ schoolYear: year, councilType, module: "student" }));
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, councilType]);

  // Khi click vào 1 row → load detail + chuyển sang view mode.
  const handleSelect = (template) => {
    setSelected(template);
    setMode("view");
    setForm({ ...template, members: [...(template.members || [])] });
  };

  const handleCreate = () => {
    setSelected(null);
    setMode("create");
    setForm({
      name: "",
      schoolYear: year,
      councilType,
      module: "student",
      description: "",
      members: buildDefaultMembers(),
    });
  };

  const handleEdit = () => {
    if (!selected) return;
    setMode("edit");
    setForm({ ...selected, members: [...(selected.members || [])] });
  };

  const handleCancel = () => {
    if (mode === "create") {
      setMode("view");
      setSelected(null);
      setForm(null);
    } else if (mode === "edit") {
      setMode("view");
      setForm({ ...selected, members: [...(selected.members || [])] });
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!form?.name?.trim()) {
      dispatch(notification({ type: "warning", message: "Vui lòng nhập tên tiểu ban." }));
      return;
    }
    const reason = validateMembers(form.members);
    if (reason) {
      dispatch(notification({ type: "warning", message: reason }));
      return;
    }
    const payload = {
      ...(selected || {}),
      ...form,
      schoolYear: form.schoolYear || year,
      councilType: form.councilType || councilType,
      module: form.module || "student",
    };
    const ok =
      mode === "create"
        ? await dispatch(insertCouncil(payload))
        : await dispatch(updateCouncil(payload));
    if (ok) {
      dispatch(notification({ type: "success", message: "Lưu tiểu ban thành công!" }));
      setMode("view");
      reload();
    }
  };

  const handleDelete = async () => {
    if (!deleteTargetId) return;
    const ok = await dispatch(deleteCouncil(deleteTargetId));
    if (ok) {
      dispatch(notification({ type: "success", message: "Đã xóa tiểu ban." }));
      if (selected?.id === deleteTargetId) {
        setSelected(null);
        setForm(null);
        setMode("view");
      }
      reload();
    }
    setDeleteAnchor(null);
    setDeleteTargetId(null);
  };

  // Member operations (chỉ apply ở edit/create mode).
  const isEditing = mode === "edit" || mode === "create";
  const setMembers = (newMembers) => setForm((f) => ({ ...f, members: newMembers }));

  const addMember = (roleKey) => {
    const cfg = getRoleConfig(roleKey);
    if (!cfg) return;
    const same = (form?.members || []).filter((m) => m.roleKey === roleKey);
    if (same.length >= cfg.max) {
      dispatch(notification({ type: "warning", message: `Tối đa ${cfg.max} ${cfg.label.toLowerCase()}.` }));
      return;
    }
    const nextOrder = (same[same.length - 1]?.orderNum || 0) + 1;
    setMembers([
      ...form.members,
      { roleKey, orderNum: nextOrder, profileId: 0, name: "", degree: "", isPhenikaa: true },
    ]);
  };

  const removeMember = (roleKey, orderNum) => {
    const cfg = getRoleConfig(roleKey);
    if (!cfg) return;
    const same = (form?.members || []).filter((m) => m.roleKey === roleKey);
    if (same.length <= cfg.min) {
      dispatch(notification({ type: "warning", message: `Cần ít nhất ${cfg.min} ${cfg.label.toLowerCase()}.` }));
      return;
    }
    setMembers(form.members.filter((m) => !(m.roleKey === roleKey && m.orderNum === orderNum)));
  };

  const updateMember = (roleKey, orderNum, patch) => {
    setMembers(
      form.members.map((m) =>
        m.roleKey === roleKey && m.orderNum === orderNum ? { ...m, ...patch } : m
      )
    );
  };

  // ---- Render ----
  const grouped = form ? groupMembersByRole(form.members) : null;

  return (
    <Card
      style={{
        padding: "12px 16px",
        height: "calc(100vh - 108px)",
        minHeight: "calc(100vh - 108px)",
        maxHeight: "calc(100vh - 108px)",
        overflow: "auto",
      }}
    >
      <form id="council-template-form" onSubmit={handleSubmit}>
        {/* Header */}
        <Grid container spacing={1} alignItems="center" style={{ marginBottom: 12 }}>
          <Grid item xs={6}>
            <Typography variant="h5">
              <b>Tiểu ban hội đồng — Hội nghị NCKH cấp trường</b>
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Tạo template hội đồng để tái sử dụng khi gán hội đồng cho từng đề tài.
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Box display="flex" justifyContent="flex-end" gridGap={8} alignItems="center">
              <TextField
                select size="small" variant="outlined" label="Loại hội đồng"
                value={councilType}
                onChange={(e) => setCouncilType(e.target.value)}
                style={{ width: 180 }}
              >
                <MenuItem value="final">Nghiệm thu</MenuItem>
              </TextField>
              <TextField
                select size="small" variant="outlined" label="Năm học"
                value={year} onChange={(e) => setYear(e.target.value)}
                style={{ width: 140 }}
              >
                {yearRange.map((y) => (
                  <MenuItem key={`year-${y}`} value={y}>{`${y} - ${y + 1}`}</MenuItem>
                ))}
              </TextField>
              <Tooltip title="Tải lại">
                <IconButton size="small" onClick={reload}><Replay /></IconButton>
              </Tooltip>
              <Button
                color="primary" variant="contained" startIcon={<Add />}
                onClick={handleCreate} disabled={isEditing}
              >
                Thêm tiểu ban
              </Button>
            </Box>
          </Grid>
        </Grid>
        <Divider style={{ marginBottom: 12 }} />

        <Grid container spacing={2}>
          {/* ---- LEFT: Danh sách tiểu ban ---- */}
          <Grid item xs={4}>
            <Box border="solid 1px #e0e0e0" borderRadius={4} bgcolor="#fafafa">
              <Box padding="8px 12px" borderBottom="solid 1px #e0e0e0" bgcolor="#f0f0f0">
                <Typography variant="subtitle2">
                  <b>Danh sách ({councilList.length})</b>
                </Typography>
              </Box>
              <Box maxHeight="calc(100vh - 260px)" style={{ overflowY: "auto" }}>
                {councilList.length === 0 && (
                  <Box padding={2} textAlign="center">
                    <Typography variant="caption" color="textSecondary">
                      Chưa có tiểu ban nào cho năm này.
                    </Typography>
                  </Box>
                )}
                {councilList.map((t) => (
                  <Box
                    key={t.id}
                    padding="10px 12px"
                    borderBottom="solid 1px #e8e8e8"
                    bgcolor={selected?.id === t.id ? "#e3f2fd" : "white"}
                    style={{ cursor: isEditing ? "not-allowed" : "pointer" }}
                    onClick={() => !isEditing && handleSelect(t)}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" style={{ fontWeight: 500 }}>
                        {t.name || "(chưa đặt tên)"}
                      </Typography>
                      <Tooltip title="Xóa">
                        <IconButton
                          size="small"
                          disabled={isEditing}
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteAnchor(e.currentTarget);
                            setDeleteTargetId(t.id);
                          }}
                        >
                          <Delete fontSize="small" style={{ color: "#f44336" }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Box display="flex" gridGap={4} marginTop={0.5}>
                      <Chip
                        size="small"
                        label={t.councilType === "progress" ? "Tiến độ" : "Nghiệm thu"}
                        style={{ height: 18, fontSize: 11 }}
                      />
                      <Chip
                        size="small"
                        label={`${(t.members || []).length} thành viên`}
                        style={{ height: 18, fontSize: 11 }}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Grid>

          {/* ---- RIGHT: Detail / Form ---- */}
          <Grid item xs={8}>
            {!form && !isEditing && (
              <Box
                border="dashed 2px #bdbdbd" borderRadius={4} padding={4}
                textAlign="center" color="#9e9e9e"
              >
                <GroupAdd style={{ fontSize: 48, color: "#bdbdbd" }} />
                <Typography variant="body1" style={{ marginTop: 8 }}>
                  Chọn 1 tiểu ban ở danh sách bên trái để xem chi tiết, hoặc
                  bấm <b>"Thêm tiểu ban"</b> để tạo mới.
                </Typography>
              </Box>
            )}

            {form && (
              <Box border="solid 1px #e0e0e0" borderRadius={4}>
                {/* Section: Metadata */}
                <Box padding="12px 16px" bgcolor="#f0f0f0" borderBottom="solid 1px #e0e0e0">
                  <Typography variant="subtitle2" style={{ color: theme.palette.primary.main }}>
                    <b>THÔNG TIN TIỂU BAN</b>
                  </Typography>
                </Box>
                <Box padding={2}>
                  <Grid container spacing={2}>
                    <Grid item xs={8}>
                      <Typography variant="body2" style={{ marginBottom: 4 }}>
                        <b>Tên tiểu ban</b> <span style={{ color: "#c62828" }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth size="small" variant="outlined" required
                        disabled={!isEditing}
                        value={form.name || ""}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" style={{ marginBottom: 4 }}>
                        <b>Loại hội đồng</b>
                      </Typography>
                      <Select
                        fullWidth variant="outlined" margin="dense"
                        disabled={!isEditing}
                        value={form.councilType || "final"}
                        onChange={(e) => setForm({ ...form, councilType: e.target.value })}
                      >
                        {/* Refactor 2026-08-26: bỏ hội đồng "Duyệt tiến độ" —
                            BCTĐ do Trưởng khoa duyệt trực tiếp. Template cũ
                            loại này vẫn xem/xoá được, chỉ không tạo mới. */}
                        <MenuItem value="final">Nghiệm thu</MenuItem>
                      </Select>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" style={{ marginBottom: 4 }}>
                        <b>Ghi chú</b>
                      </Typography>
                      <TextField
                        fullWidth size="small" variant="outlined" multiline minRows={2}
                        disabled={!isEditing}
                        value={form.description || ""}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                      />
                    </Grid>
                  </Grid>
                </Box>

                {/* Section: Members */}
                <Box padding="12px 16px" bgcolor="#f0f0f0" borderTop="solid 1px #e0e0e0" borderBottom="solid 1px #e0e0e0">
                  <Typography variant="subtitle2" style={{ color: theme.palette.primary.main }}>
                    <b>THÀNH VIÊN</b>
                  </Typography>
                </Box>
                <Box padding={2}>
                  {COUNCIL_ROLES.map((role) => {
                    const list = grouped[role.key] || [];
                    return (
                      <Box key={role.key} marginBottom={2}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={1}>
                          <Typography variant="body2">
                            <b>{role.label}</b>
                            <span style={{ color: "#9e9e9e", marginLeft: 8, fontSize: 12 }}>
                              ({list.length}{role.max > 1 ? `/${role.max}` : ""})
                            </span>
                          </Typography>
                          {isEditing && role.max > role.min && (
                            <Button
                              size="small" variant="outlined" color="primary"
                              startIcon={<Add />}
                              disabled={list.length >= role.max}
                              onClick={() => addMember(role.key)}
                            >
                              Thêm {role.label.toLowerCase()}
                            </Button>
                          )}
                        </Box>
                        {list.map((m) => (
                          <MemberRow
                            key={`${m.roleKey}-${m.orderNum}`}
                            member={m}
                            role={role}
                            profileList={profileList}
                            isEditing={isEditing}
                            canRemove={list.length > role.min}
                            onChange={(patch) => updateMember(m.roleKey, m.orderNum, patch)}
                            onRemove={() => removeMember(m.roleKey, m.orderNum)}
                          />
                        ))}
                      </Box>
                    );
                  })}
                </Box>

                {/* Action footer */}
                <Box padding="12px 16px" borderTop="solid 1px #e0e0e0" display="flex" justifyContent="flex-end" gridGap={8}>
                  {mode === "view" && selected && (
                    <Button color="primary" variant="contained" startIcon={<Edit />} onClick={handleEdit}>
                      Chỉnh sửa
                    </Button>
                  )}
                  {isEditing && (
                    <>
                      <Button variant="outlined" startIcon={<Close />} onClick={handleCancel}>
                        Hủy
                      </Button>
                      <Button type="submit" color="primary" variant="contained" startIcon={<Save />}>
                        Lưu
                      </Button>
                    </>
                  )}
                </Box>
              </Box>
            )}
          </Grid>
        </Grid>
      </form>

      {/* Popover xác nhận xóa */}
      <Popover
        open={!!deleteAnchor}
        anchorEl={deleteAnchor}
        onClose={() => {
          setDeleteAnchor(null);
          setDeleteTargetId(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Box padding={2} width={240}>
          <Typography variant="body2" style={{ marginBottom: 8 }}>
            Xác nhận xóa tiểu ban?
          </Typography>
          <Box display="flex" gridGap={8} justifyContent="flex-end">
            <Button
              size="small" variant="outlined"
              onClick={() => {
                setDeleteAnchor(null);
                setDeleteTargetId(null);
              }}
            >
              Hủy
            </Button>
            <Button size="small" color="secondary" variant="contained" onClick={handleDelete}>
              Xóa
            </Button>
          </Box>
        </Box>
      </Popover>
    </Card>
  );
}

// MemberRow — render 1 thành viên: checkbox GV trong trường + tên (autocomplete
// nếu Phenikaa, text input nếu ngoài) + học hàm/vị + nút xóa.
function MemberRow({ member, role, profileList, isEditing, canRemove, onChange, onRemove }) {
  const isPhenikaa = member.isPhenikaa !== false; // mặc định true
  const selectedProfile = isPhenikaa
    ? profileList.find((p) => p.id === member.profileId) || null
    : null;

  return (
    <Box
      display="flex"
      gridGap={8}
      alignItems="center"
      padding="6px 8px"
      bgcolor="#fafafa"
      borderRadius={4}
      marginBottom={6}
      border="solid 1px #eee"
    >
      <Box width={150} flexShrink={0}>
        <FormControlLabel
          disabled={!isEditing}
          control={
            <Checkbox
              size="small"
              color="primary"
              checked={isPhenikaa}
              onChange={(e) => {
                const newIsPhenikaa = e.target.checked;
                onChange({
                  isPhenikaa: newIsPhenikaa,
                  // Reset profile_id + clear name khi chuyển kiểu
                  profileId: newIsPhenikaa ? member.profileId : 0,
                  name: newIsPhenikaa ? member.name : member.name,
                  degree: newIsPhenikaa ? member.degree : member.degree,
                });
              }}
            />
          }
          label={<Typography variant="caption">GV trong trường</Typography>}
        />
      </Box>
      <Box flexGrow={1} minWidth={0}>
        {isPhenikaa ? (
          <Autocomplete
            disabled={!isEditing}
            options={profileList}
            getOptionLabel={(o) => o?.name || ""}
            value={selectedProfile}
            onChange={(_, val) =>
              onChange({
                profileId: val?.id || 0,
                name: val?.name || "",
                degree: val?.degree || "",
              })
            }
            renderInput={(params) => (
              <TextField
                {...params} size="small" variant="outlined" placeholder="Chọn GV"
              />
            )}
          />
        ) : (
          <TextField
            fullWidth size="small" variant="outlined"
            placeholder="Tên thành viên"
            disabled={!isEditing}
            value={member.name || ""}
            onChange={(e) => onChange({ name: e.target.value })}
            InputProps={{
              startAdornment: <Person fontSize="small" style={{ color: "#9e9e9e", marginRight: 6 }} />,
            }}
          />
        )}
      </Box>
      <Box width={140} flexShrink={0}>
        <TextField
          fullWidth size="small" variant="outlined"
          placeholder="Học hàm/vị"
          disabled={!isEditing || isPhenikaa}
          value={member.degree || ""}
          onChange={(e) => onChange({ degree: e.target.value })}
        />
      </Box>
      {isEditing && canRemove && (
        <Tooltip title={`Xóa ${role.label.toLowerCase()} này`}>
          <IconButton size="small" onClick={onRemove}>
            <Close fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {(!isEditing || !canRemove) && <Box width={28} />}
    </Box>
  );
}

export default StudentProjectCouncil;
